import React, { useEffect } from 'react';
import { X as IconX, CheckCircle, AlertOctagon } from 'lucide-react';

// Função para reproduzir um som de erro de forma programática usando Web Audio API
const playErrorSound = () => {
    try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        
        osc.connect(gain);
        gain.connect(audioCtx.destination);
        
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(150, audioCtx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(100, audioCtx.currentTime + 0.35);
        
        gain.gain.setValueAtTime(0.3, audioCtx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.4);
        
        osc.start();
        osc.stop(audioCtx.currentTime + 0.4);
    } catch (e) {
        console.warn("Não foi possível reproduzir o áudio de erro:", e);
    }
};

/**
 * Um modal de notificação simples que aparece como um toast/banner.
 * Exibe uma mensagem de sucesso ou erro e desaparece após alguns segundos.
 * @param {object} props - Propriedades do componente.
 * @param {string|object} props.message - A mensagem a ser exibida (ou objeto {text, type}).
 * @param {function} props.onClose - A função a ser chamada para fechar o modal.
 */
const NotificationModal = ({ message, onClose }) => {
    // Permite que 'message' seja uma string ou um objeto { text, type }
    const msgText = typeof message === 'object' && message ? message.text : message;
    const msgType = typeof message === 'object' && message ? message.type : 'success';

    useEffect(() => {
        if (msgText) {
            const timer = setTimeout(() => {
                onClose();
            }, 3000); // 3 segundos para dar tempo de ler mensagens maiores de erro

            return () => clearTimeout(timer);
        }
    }, [msgText, onClose]);

    // Executa o som de aviso apenas quando a notificação é de erro e é exibida
    useEffect(() => {
        if (msgText && msgType === 'error') {
            playErrorSound();
        }
    }, [msgText, msgType]);

    if (!msgText) {
        return null;
    }

    const isError = msgType === 'error';
    
    // Classes de estilo dinâmicas baseadas no tipo de notificação (sucesso ou erro)
    const containerClasses = isError
        ? "fixed top-5 right-5 bg-red-50 dark:bg-red-950/40 p-4 rounded-xl shadow-2xl border border-red-200 dark:border-red-900/50 flex items-center gap-4 z-50 animate-fade-in-down"
        : "fixed top-5 right-5 bg-white dark:bg-slate-800 p-4 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 flex items-center gap-4 z-50 animate-fade-in-down";

    const textClasses = isError
        ? "text-sm font-semibold text-red-900 dark:text-red-100"
        : "text-sm font-semibold text-slate-900 dark:text-slate-100";

    const buttonClasses = isError
        ? "p-1.5 rounded-full text-red-500 hover:bg-red-200/50 dark:hover:bg-red-900/30 transition-colors ml-auto focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        : "p-1.5 rounded-full text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors ml-auto focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-slate-800";

    return (
        <div className={containerClasses}>
            {isError ? (
                <AlertOctagon className="h-6 w-6 text-red-600 dark:text-red-400 flex-shrink-0 animate-bounce" />
            ) : (
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" />
            )}
            <p className={textClasses}>{msgText}</p>
            <button 
                onClick={onClose} 
                className={buttonClasses}
                aria-label="Fechar notificação"
            >
                <IconX className="h-5 w-5" />
            </button>
        </div>
    );
};

export default NotificationModal;
